import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import { format, parseISO } from 'date-fns';
import { calendarService } from '../../../services/calendarService';
import { workflowService } from '../../../services/workflowService';

const ConsultationDetailModal = ({ consultation, onClose, onStatusUpdate, onRefresh }) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [meetingNotes, setMeetingNotes] = useState(consultation?.meetingNotes || '');
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [leadScore, setLeadScore] = useState(null);
  const [salesPipeline, setSalesPipeline] = useState(null);
  const [automatedTasks, setAutomatedTasks] = useState([]);
  const [workflowExecutions, setWorkflowExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'confirmed': 'bg-[#D1F5F0] text-[#1FB5A5] border-[#1FB5A5]/30',
      'completed': 'bg-green-100 text-green-800 border-green-200',
      'cancelled': 'bg-red-100 text-red-800 border-red-200'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const formatDateTime = (dateString) => {
    try {
      return format(parseISO(dateString), 'EEEE, MMMM dd, yyyy • h:mm a');
    } catch {
      return 'Invalid date';
    }
  };

  const handleStatusChange = async (newStatus) => {
    setIsUpdating(true);
    try {
      await onStatusUpdate(consultation?.id, newStatus);
      await onRefresh();
      onClose();
    } catch (error) {
      alert('Failed to update status');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleSaveNotes = async () => {
    setIsSavingNotes(true);
    try {
      await calendarService?.updateBooking(consultation?.id, { meetingNotes });
      await onRefresh();
      alert('Notes saved successfully');
    } catch (error) {
      alert('Failed to save notes');
    } finally {
      setIsSavingNotes(false);
    }
  };

  const handleCopyEmail = () => {
    navigator?.clipboard?.writeText(consultation?.clientEmail);
    alert('Email copied to clipboard');
  };

  const handleCopyPhone = () => {
    navigator?.clipboard?.writeText(consultation?.clientPhone || '');
    alert('Phone copied to clipboard');
  };

  React.useEffect(() => {
    if (consultation?.id) {
      loadWorkflowData();
    }
  }, [consultation?.id]);

  const loadWorkflowData = async () => {
    try {
      setLoading(true);
      const [scoreData, pipelineData, tasksData, executionsData] = await Promise.all([
        workflowService?.getLeadScore(consultation?.id),
        workflowService?.getSalesPipeline(consultation?.id),
        workflowService?.getAutomatedTasks(consultation?.id),
        workflowService?.getWorkflowExecutions(consultation?.id)
      ]);
      setLeadScore(scoreData);
      setSalesPipeline(pipelineData);
      setAutomatedTasks(tasksData || []);
      setWorkflowExecutions(executionsData || []);
    } catch (error) {
      console.error('Failed to load workflow data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTaskStatusUpdate = async (taskId, newStatus) => {
    try {
      await workflowService?.updateAutomatedTask(taskId, { status: newStatus });
      await loadWorkflowData();
    } catch (error) {
      alert('Failed to update task status');
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-[#1FB5A5]';
    if (score >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'urgent': 'bg-red-100 text-red-800 border-red-200',
      'high': 'bg-orange-100 text-orange-800 border-orange-200',
      'medium': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'low': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors?.[priority] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getTaskStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'in_progress': 'bg-[#E6F0F5] text-[#002B45] border-[#002B45]/20',
      'completed': 'bg-green-100 text-green-800 border-green-200',
      'cancelled': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getPipelineStageColor = (stage) => {
    const colors = {
      'lead': 'bg-gray-100 text-gray-800 border-gray-200',
      'qualified': 'bg-[#E6F0F5] text-[#002B45] border-[#002B45]/20',
      'proposal': 'bg-purple-100 text-purple-800 border-purple-200',
      'negotiation': 'bg-orange-100 text-orange-800 border-orange-200',
      'closed_won': 'bg-green-100 text-green-800 border-green-200',
      'closed_lost': 'bg-red-100 text-red-800 border-red-200'
    };
    return colors?.[stage] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-elevation-3 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Consultation Details</h2>
            <p className="text-sm text-muted-foreground mt-1">ID: {consultation?.id?.slice(0, 8)}</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
          />
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 px-6">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('details')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'details' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab('workflow')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'workflow' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Workflow & Tasks
            </button>
            <button
              onClick={() => setActiveTab('pipeline')}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'pipeline' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Sales Pipeline
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {activeTab === 'details' && (
            <>
              {/* Status Section */}
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-foreground">Status Management</h3>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(consultation?.status)}`}>
                    {consultation?.status?.charAt(0)?.toUpperCase() + consultation?.status?.slice(1)}
                  </span>
                </div>
                <Select
                  label="Update Status"
                  value={consultation?.status}
                  onChange={handleStatusChange}
                  options={statusOptions}
                  disabled={isUpdating}
                />
              </div>

              {/* Client Information */}
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="User" size={18} className="text-primary" />
                  Client Information
                </h3>
                <div className="space-y-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground">Name</p>
                      <p className="text-sm font-medium text-foreground">{consultation?.clientName}</p>
                    </div>
                  </div>
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground">Email</p>
                      <p className="text-sm font-medium text-foreground">{consultation?.clientEmail}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleCopyEmail}
                      iconName="Copy"
                      iconPosition="left"
                    >
                      Copy
                    </Button>
                  </div>
                  {consultation?.clientPhone && (
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs text-muted-foreground">Phone</p>
                        <p className="text-sm font-medium text-foreground">{consultation?.clientPhone}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleCopyPhone}
                        iconName="Copy"
                        iconPosition="left"
                      >
                        Copy
                      </Button>
                    </div>
                  )}
                  {consultation?.company && (
                    <div>
                      <p className="text-xs text-muted-foreground">Company</p>
                      <p className="text-sm font-medium text-foreground flex items-center gap-2">
                        <Icon name="Building2" size={14} />
                        {consultation?.company}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Consultation Details */}
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="Calendar" size={18} className="text-primary" />
                  Consultation Details
                </h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Title</p>
                    <p className="text-sm font-medium text-foreground">{consultation?.title}</p>
                  </div>
                  {consultation?.description && (
                    <div>
                      <p className="text-xs text-muted-foreground">Description</p>
                      <p className="text-sm text-foreground">{consultation?.description}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-muted-foreground">Service Interest</p>
                    <p className="text-sm font-medium text-foreground">{consultation?.serviceInterest || 'Not specified'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Scheduled Time</p>
                    <p className="text-sm font-medium text-foreground">{formatDateTime(consultation?.scheduledStart)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Duration</p>
                    <p className="text-sm font-medium text-foreground">{consultation?.durationMinutes} minutes</p>
                  </div>
                </div>
              </div>

              {/* Meeting Notes */}
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="FileText" size={18} className="text-primary" />
                  Meeting Notes
                </h3>
                <textarea
                  value={meetingNotes}
                  onChange={(e) => setMeetingNotes(e?.target?.value)}
                  placeholder="Add notes about this consultation..."
                  rows={6}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                />
                <div className="mt-2 flex justify-end">
                  <Button
                    onClick={handleSaveNotes}
                    loading={isSavingNotes}
                    iconName="Save"
                    iconPosition="left"
                    size="sm"
                  >
                    Save Notes
                  </Button>
                </div>
              </div>

              {/* Communication Actions */}
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="MessageSquare" size={18} className="text-primary" />
                  Quick Actions
                </h3>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(`mailto:${consultation?.clientEmail}`, '_blank')}
                    iconName="Mail"
                    iconPosition="left"
                  >
                    Send Email
                  </Button>
                  {consultation?.clientPhone && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(`tel:${consultation?.clientPhone}`, '_blank')}
                      iconName="Phone"
                      iconPosition="left"
                    >
                      Call Client
                    </Button>
                  )}
                </div>
              </div>

              {/* Metadata */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="text-xs font-semibold text-muted-foreground mb-2">Metadata</h3>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <p className="text-muted-foreground">Created</p>
                    <p className="text-foreground font-medium">{formatDateTime(consultation?.createdAt)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Last Updated</p>
                    <p className="text-foreground font-medium">{formatDateTime(consultation?.updatedAt)}</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === 'workflow' && (
            <>
              {/* Lead Score Section */}
              {leadScore && (
                <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Icon name="TrendingUp" size={20} className="text-primary" />
                    Lead Score Analysis
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-xs text-muted-foreground mb-1">Engagement</p>
                      <p className={`text-2xl font-bold ${getScoreColor(leadScore?.engagementScore)}`}>
                        {leadScore?.engagementScore}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-xs text-muted-foreground mb-1">Company Size</p>
                      <p className={`text-2xl font-bold ${getScoreColor(leadScore?.companySizeScore)}`}>
                        {leadScore?.companySizeScore}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-xs text-muted-foreground mb-1">Service Fit</p>
                      <p className={`text-2xl font-bold ${getScoreColor(leadScore?.serviceFitScore)}`}>
                        {leadScore?.serviceFitScore}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-xs text-muted-foreground mb-1">Budget</p>
                      <p className={`text-2xl font-bold ${getScoreColor(leadScore?.budgetScore)}`}>
                        {leadScore?.budgetScore}
                      </p>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-xs text-muted-foreground mb-1">Timeline</p>
                      <p className={`text-2xl font-bold ${getScoreColor(leadScore?.timelineScore)}`}>
                        {leadScore?.timelineScore}
                      </p>
                    </div>
                    <div className="bg-primary/10 rounded-lg p-3 border-2 border-primary">
                      <p className="text-xs text-muted-foreground mb-1">Total Score</p>
                      <p className={`text-2xl font-bold ${getScoreColor(leadScore?.totalScore)}`}>
                        {leadScore?.totalScore}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Automated Tasks */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Icon name="CheckSquare" size={20} className="text-primary" />
                  Automated Follow-up Tasks
                </h3>
                {automatedTasks?.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Icon name="Inbox" size={48} className="mx-auto mb-2 opacity-50" />
                    <p>No automated tasks generated yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {automatedTasks?.map((task) => (
                      <div key={task?.id} className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <h4 className="font-medium text-foreground">{task?.title}</h4>
                            {task?.description && (
                              <p className="text-sm text-muted-foreground mt-1">{task?.description}</p>
                            )}
                          </div>
                          <div className="flex gap-2 ml-4">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(task?.priority)}`}>
                              {task?.priority}
                            </span>
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getTaskStatusColor(task?.status)}`}>
                              {task?.status?.replace('_', ' ')}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <div className="flex items-center gap-4">
                            {task?.dueDate && (
                              <span className="flex items-center gap-1">
                                <Icon name="Clock" size={14} />
                                Due: {format(parseISO(task?.dueDate), 'MMM dd, yyyy')}
                              </span>
                            )}
                            {task?.assignedToName && (
                              <span className="flex items-center gap-1">
                                <Icon name="User" size={14} />
                                {task?.assignedToName}
                              </span>
                            )}
                          </div>
                          {task?.status !== 'completed' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleTaskStatusUpdate(task?.id, 'completed')}
                            >
                              Mark Complete
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Workflow Executions */}
              {workflowExecutions?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Icon name="Workflow" size={20} className="text-primary" />
                    Workflow Execution History
                  </h3>
                  <div className="space-y-2">
                    {workflowExecutions?.map((execution) => (
                      <div key={execution?.id} className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm text-foreground">{execution?.templateName}</p>
                          <p className="text-xs text-muted-foreground">
                            Started: {format(parseISO(execution?.startedAt), 'MMM dd, yyyy h:mm a')}
                          </p>
                        </div>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          execution?.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {execution?.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {activeTab === 'pipeline' && (
            <>
              {salesPipeline ? (
                <>
                  {/* Pipeline Stage */}
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                      <Icon name="Target" size={20} className="text-primary" />
                      Sales Pipeline Status
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-white rounded-lg p-4">
                        <p className="text-xs text-muted-foreground mb-2">Current Stage</p>
                        <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium border ${getPipelineStageColor(salesPipeline?.currentStage)}`}>
                          {salesPipeline?.currentStage?.replace('_', ' ')?.toUpperCase()}
                        </span>
                      </div>
                      <div className="bg-white rounded-lg p-4">
                        <p className="text-xs text-muted-foreground mb-2">Win Probability</p>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-primary h-2 rounded-full transition-all"
                              style={{ width: `${salesPipeline?.probability}%` }}
                            />
                          </div>
                          <span className="text-sm font-bold text-foreground">{salesPipeline?.probability}%</span>
                        </div>
                      </div>
                      {salesPipeline?.estimatedValue && (
                        <div className="bg-white rounded-lg p-4">
                          <p className="text-xs text-muted-foreground mb-2">Estimated Value</p>
                          <p className="text-xl font-bold text-foreground">
                            ${salesPipeline?.estimatedValue?.toLocaleString()}
                          </p>
                        </div>
                      )}
                      {salesPipeline?.expectedCloseDate && (
                        <div className="bg-white rounded-lg p-4">
                          <p className="text-xs text-muted-foreground mb-2">Expected Close Date</p>
                          <p className="text-sm font-medium text-foreground">
                            {format(parseISO(salesPipeline?.expectedCloseDate), 'MMMM dd, yyyy')}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Stage History */}
                  {salesPipeline?.stageHistory?.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                        <Icon name="History" size={20} className="text-primary" />
                        Stage Progression History
                      </h3>
                      <div className="space-y-2">
                        {salesPipeline?.stageHistory?.map((history, index) => (
                          <div key={index} className="flex items-center gap-3">
                            <div className="w-2 h-2 rounded-full bg-primary" />
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getPipelineStageColor(history?.stage)}`}>
                              {history?.stage?.replace('_', ' ')?.toUpperCase()}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {format(parseISO(history?.entered_at), 'MMM dd, yyyy h:mm a')}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Pipeline Notes */}
                  {salesPipeline?.notes && (
                    <div>
                      <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                        <Icon name="FileText" size={20} className="text-primary" />
                        Pipeline Notes
                      </h3>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-foreground">{salesPipeline?.notes}</p>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Icon name="Inbox" size={64} className="mx-auto mb-4 opacity-50" />
                  <p className="text-lg">No pipeline data available</p>
                  <p className="text-sm mt-2">Pipeline data will be created when consultation is completed</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConsultationDetailModal;